import { Client } from "@/Common/Api/Client";
import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { EnumMyleLoginPermission } from "@/Common/Models/EnumMyleLoginPermission";
import { MyleTabKey } from "@/Common/Models/MyleMenu/enums/MyleTabKey";
import { DocumentsCounters } from "@/Common/Models/MyleMenu/Tooltip/DocumentsCounter";
import { PortalLinkedItemType } from "@/Common/Models/Portal/Enums/PortalLinkedItemType";
import { SharedItem } from "@/Common/Models/Portal/SharedItem/SharedItem";
import { DocumentSortOrderType, SortDirection } from "@/Common/Models/UI/Enums";
import { integrationBus } from "@/Common/Modularity/IntegrationBus/IntegrationBus";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import * as SubServerOperations from "@/Common/Operations/operations.server.sub";
import { DocumentClass } from "@/Common/Resources/Lookups/Enums/DocumentClasses";
import { BaseAction } from "@/Common/Stores/Actions/BaseAction";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { DocumentPath } from "@/Documents/Api/Dto/Response/DocumentPath";
import { MedicalDocumentsCount } from "@/Documents/Api/Dto/Response/MedicalDocumentCount";
import { InboundFaxesMailboxActions } from "@/Documents/Components/InboundFax/InboundFaxesMailboxActions";
import { MedicalDocumentsSearchRequestedEvent } from "@/Documents/IntegrationBus/DocumentEvents";
import { DocumentSearchSettings } from "@/Documents/Models/Settings/DocumentSearchSettings";
import { IDocumentCreateEditUIModel, IDocumentInformation } from "@/Documents/Models/UI/IDocumentCreateEditUIModel";
import { IDocumentsTabModel } from "@/Documents/Models/UI/IDocumentsTabModel";
import { fetchPrescriptionsRenewalCounters } from "@/PrescriptionRenewals/Api/PrescriptionRenewalsClient";

class DocumentsAction extends BaseAction {
    private helper: MyleHelper;

    constructor() {
        super();
        this.helper = new MyleHelper();
    }

    public ApproveDocuments(docIDs: string[], subOperation: number): void {
        let requestData = {
            MedicalDocumentIds: docIDs
        };

        this.Execute(ServerOperations.Server.Myle_MedicalDocuments_ApproveDocuments, requestData, subOperation);
    }

    public SaveDocumentNote(noteText: string, documentID: string, subOperation: number): void {
        let requestData = {
            MedicalDocumentId: documentID,
            Note: noteText
        };
        this.Execute(ServerOperations.Server.Myle_MedicalDocuments_CreateNote, requestData, subOperation);
    }

    public SearchDocuments(model: IDocumentsTabModel, resetPageNumber?: boolean): void {
        let self = this;

        const requestData = {
            FromDueDate: null,
            ToDueDate: null,
            PageNumber: null,
            PageSize: null,
            OrderDirection: SortDirection.DESC,
            OrderBy: DocumentSortOrderType.DocumentDate,
            SetOrphansOnly: false,
            DraftsOnly: false,
            SubClassId: null,
            SearchString: "",
            PracticiansId: [],
            IsEveryPracticianSelected: false,
            StatusApproved: false,
            StatusNotApproved: false,
            StatusAnnotated: false,
            StatusApprovedByColleague: false,
            IsUrgentSelected: false,
            OrphansOnly: false,
            IsNotUrgentSelected: false,
            LanguageCode: "",
            PatientId: "",
            PortalSyncStatuses: [],
            PortalSyncUserContactIds: [],
            SiteIds: null,
            IsDeactivatedAccountsOnly: false,
            ClassIds: []
        };

        var id = model.get("PatientContactId");
        if (id == null || id === "") { delete requestData.PatientId; }
        else { requestData.PatientId = id; }

        if (model.get("DateFrom") != null) {
            requestData.FromDueDate = convertLocalDateOffsetToUtc(model.get("DateFrom"));
        }

        if (model.get("DateTo") != null) {
            requestData.ToDueDate = convertLocalDateOffsetToUtc(model.get("DateTo"));
        }

        requestData.PageSize = model.get("SelectedPageSizeOption");

        if (resetPageNumber) {
            requestData.PageNumber = 1;
        }
        else {
            requestData.PageNumber = model.get("SelectedPageNumber");
        }

        if (model.get("SearchResultsSortColumnDirection")) {
            requestData.OrderDirection = model.get("SearchResultsSortColumnDirection");
        }

        if (model.get("SearchResultsSortColumn")) {
            requestData.OrderBy = model.get("SearchResultsSortColumn");
        }

        if (model.get("PortalStatuses")) {
            var selectedPortalStatus = [];
            model.get("PortalStatuses").toArray().forEach(x => {
                if (x.Checked)
                    selectedPortalStatus.push(x.Id);
            });
            requestData.PortalSyncStatuses = selectedPortalStatus;
        }

        if (model.get("ShowOnlySelectedSites")) {
            requestData.SiteIds = this.helper
                .GetCheckedItemsFromICheckableList(model.get("Sites"))
                .toArray()
                .filter(x => x.Checked)
                .map(x => x.Id);
        }

        var sentByIds = model.get("SentByGlobalCache").toArray().filter(x => x.Checked == true);
        if (sentByIds != null && sentByIds.length > 0) {
            requestData.PortalSyncUserContactIds = sentByIds.map(x => x.ContactId);
        }

        if (model.get("SelectedDocumentSubclass")) {
            requestData.SubClassId = model.get("SelectedDocumentSubclass").LookupId;
        }

        requestData.SearchString = model.get("GlobalDocumentSearchText");
        requestData.IsEveryPracticianSelected = model.get("CliniciansGlobalCache").filter(c => c.Checked === false && c.Visible === true).size === 0;

        for (const status of model.get("Status").toArray()) {
            switch (status.Id) {
                case "status_approved":
                    requestData.StatusApproved = status.Checked;
                    break;
                case "status_not_approved":
                    requestData.StatusNotApproved = status.Checked;
                    break;
                case "status_annotated":
                    requestData.StatusAnnotated = status.Checked;
                    break;
                case "status_approved_by_colleague":
                    requestData.StatusApprovedByColleague = status.Checked;
            }
        }

        model.get("Urgency").forEach(urgency => {
            if (urgency.Id === "urgency_urgent")
                requestData.IsUrgentSelected = urgency.Checked;
            if (urgency.Id === "urgency_not_urgent")
                requestData.IsNotUrgentSelected = urgency.Checked;
        });

        if (model.get("SelectedView") === "orphan") {
            requestData.PracticiansId = [];
            requestData.IsEveryPracticianSelected = true;
            requestData.OrphansOnly = true;
        }
        else if (model.get("SelectedView") === "deactivatedAccount") {
            requestData.IsDeactivatedAccountsOnly = true;
            let isNonSelected = model.get("DeactivatedAccountCliniciansCache")
                .filter(c => c.Checked === false && c.Visible === true)
                .size === model.get("DeactivatedAccountCliniciansCache").filter(x => x.Visible).size;
            requestData.PracticiansId = isNonSelected
                ? model.get("DeactivatedAccountCliniciansCache").map(j => j.Id).toArray()
                : model.get("DeactivatedAccountCliniciansCache")
                    .filter(i => i.Checked === true && i.Visible === true)
                    .map(j => j.Id)
                    .toArray();
        }
        else if (!requestData.IsEveryPracticianSelected) {
            requestData.PracticiansId = model.get("CliniciansGlobalCache")
                .filter(i => i.Checked === true && i.Visible === true)
                .map(j => j.Id)
                .toArray();
        }

        if (model.get("SentByGlobalCache").some(x => x.Checked == true)) {
            requestData.PortalSyncUserContactIds = model.get("SentByGlobalCache")
                .filter(i => i.Checked === true && i.Visible === true)
                .map(j => j.Id).toArray();
        }
        requestData.LanguageCode = sessionStore.userLanguageCode;
        requestData.ClassIds = model.get("SelectedDocumentSubclass") ? [] : model.get("Classes")?.filter(p => p.Checked).map(p => p.Id).toArray();

        // Throtlled
        if (documentsStore.timeoutReferencRefreshDocs !== -1) {
            clearTimeout(documentsStore.timeoutReferencRefreshDocs);
            documentsStore.timeoutReferencRefreshDocs = -1;
        }

        documentsStore.timeoutReferencRefreshDocs = window.setTimeout(() => {
            integrationBus.emit(new MedicalDocumentsSearchRequestedEvent({}));
            self.Execute(ServerOperations.Server.Myle_MedicalDocuments_Search, requestData);
        }, 250);
    }

    public SearchOrphanDocuments(model: IDocumentsTabModel): void {
        let self = this;
        let requestData = {
            FromDueDate: null,
            ToDueDate: null,
            OrderDirection: SortDirection.DESC,
            OrderBy: DocumentSortOrderType.DocumentDate,
            SetOrphansOnly: false,
            DraftsOnly: false,
            SubClassId: null,
            SearchString: "",
            PracticiansId: [],
            StatusApproved: false,
            StatusNotApproved: false,
            StatusAnnotated: false,
            StatusApprovedByColleague: false,
            IsUrgentSelected: false,
            IsNotUrgentSelected: false,
            LanguageCode: "",
            OrphansOnly: true,
            IsEveryPracticianSelected: true,
            PageSize: model.get("SelectedPageSizeOption"),
            PatientId: "",
            PageNumber: 1,
            SiteIds: null

        };

        let id = model.get("PatientContactId");
        if (id == null || id === "") { delete requestData.PatientId; }
        else { requestData.PatientId = id; }

        if (model.get("DateFrom") != null) {
            requestData.FromDueDate = model.get("DateFrom");
        }

        if (model.get("DateTo") != null) {
            requestData.ToDueDate = model.get("DateTo");
        }

        requestData.PageSize = model.get("SelectedPageSizeOption");

        if (model.get("SearchResultsSortColumnDirection")) {
            requestData.OrderDirection = model.get("SearchResultsSortColumnDirection");
        }

        if (model.get("SearchResultsSortColumn")) {
            requestData.OrderBy = model.get("SearchResultsSortColumn");
        }

        if (model.get("SelectedDocumentSubclass")) {
            requestData.SubClassId = model.get("SelectedDocumentSubclass").LookupId;
        }

        if (model.get("ShowOnlySelectedSites")) {
            requestData.SiteIds = this.helper
                .GetCheckedItemsFromICheckableList(model.get("Sites"))
                .toArray()
                .filter(x => x.Checked)
                .map(x => x.Id);
        }

        requestData.SearchString = model.get("GlobalDocumentSearchText");
        requestData.IsEveryPracticianSelected = model.get("CliniciansGlobalCache").filter(c => c.Checked === false && c.Visible === true).size === 0;

        for (const status of model.get("Status").toArray()) {
            switch (status.Id) {
                case "status_approved":
                    requestData.StatusApproved = status.Checked;
                    break;
                case "status_not_approved":
                    requestData.StatusNotApproved = status.Checked;
                    break;
                case "status_annotated":
                    requestData.StatusAnnotated = status.Checked;
                    break;
                case "status_approved_by_colleague":
                    requestData.StatusApprovedByColleague = status.Checked;
            }
        }

        model.get("Urgency").forEach(urgency => {
            if (urgency.Id === "urgency_urgent")
                requestData.IsUrgentSelected = urgency.Checked;
            if (urgency.Id === "urgency_not_urgent")
                requestData.IsNotUrgentSelected = urgency.Checked;
        });

        requestData.LanguageCode = sessionStore.userLanguageCode;

        // Throlle
        if (documentsStore.timeoutReferencRefreshDocs !== -1) {
            clearTimeout(documentsStore.timeoutReferencRefreshDocs);
            documentsStore.timeoutReferencRefreshDocs = -1;
        }
        documentsStore.timeoutReferencRefreshDocs = window.setTimeout(() => {
            integrationBus.emit(new MedicalDocumentsSearchRequestedEvent({}));
            self.Execute(ServerOperations.Server.Myle_MedicalDocuments_Search, requestData);
        }, 250);
    }

    /**
     * refresh document count.
     * @param {IDocumentsTabModel} model .
    */
    public async setUnmanagedDocumentCount(model?: IDocumentsTabModel): Promise<void> {
        const patientId = model?.get("PatientContactId");
        const documentData = {
            ResponsibleProviderContactId: sessionStore.individualContactId,
            PatientId: patientId || undefined // omit if empty
        };

        // Throlle
        if (documentsStore.timeoutReferencRefreshDocsCount !== -1) {
            clearTimeout(documentsStore.timeoutReferencRefreshDocsCount);
            documentsStore.timeoutReferencRefreshDocsCount = -1;
        }

        documentsStore.timeoutReferencRefreshDocsCount = window.setTimeout(async () => {
            const unmanagedDocResponse = await Client.postAsync<MedicalDocumentsCount>(ServerOperations.Server.Myle_MedicalDocuments_CountMedicalDocuments, documentData);

            if (!unmanagedDocResponse.isSuccess) return;

            let emitChange: boolean = false;
            const unmanagedDocumentsCount = unmanagedDocResponse.data.NonApprovedDocumentCount;
            const urgentDocumentsCount = unmanagedDocResponse.data.NonApprovedAndUrgentDocumentCount;

            if (unmanagedDocumentsCount !== documentsStore.DocumentsModel.get("DocumentNonApprovedCount")) {
                documentsStore.DocumentsModel = documentsStore.DocumentsModel.set("DocumentNonApprovedCount", unmanagedDocumentsCount);
                emitChange = true;
            }
            if (urgentDocumentsCount !== documentsStore.DocumentsModel.get("DocumentNonApprovedAndUrgentCount")) {
                documentsStore.DocumentsModel = documentsStore.DocumentsModel.set("DocumentNonApprovedAndUrgentCount", urgentDocumentsCount);
                emitChange = true;
            }
            if (patientId) {
                const unmanagedDocumentsCountForPatient = unmanagedDocResponse.data.NonApprovedDocumentCountForPatient;
                if (unmanagedDocumentsCountForPatient !== documentsStore.DocumentsModel.get("DocumentNonApprovedCountForPatient")) {
                    documentsStore.DocumentsModel = documentsStore.DocumentsModel.set("DocumentNonApprovedCountForPatient", unmanagedDocumentsCountForPatient);
                    emitChange = true;
                }
            }

            const documentTab = cacheStore.Tabs.filter(tab => tab.key === MyleTabKey.Documents).firstOrDefault();
            const documentsCountHasChanged = documentTab.topCount !== unmanagedDocumentsCount || documentTab.bottomCount !== urgentDocumentsCount;
            if (documentTab && documentsCountHasChanged) {
                const documentsCounters = new DocumentsCounters(unmanagedDocumentsCount, 0, urgentDocumentsCount, 0);
                cacheStore.UpdateTab(
                    MyleTabKey.Documents,
                    unmanagedDocumentsCount,
                    urgentDocumentsCount,
                    null,
                    null,
                    true,
                    { documentsCounters: documentsCounters }
                );

                cacheStore.EmitChangeToListeners();
            }
            if (emitChange) {
                documentsStore.EmitChangeToListeners();
            }
            if (sessionStore.hasPermission(EnumMyleLoginPermission.InboundFaxPermission))
                InboundFaxesMailboxActions.updateInboundFaxUnmanagedFaxCount(documentsStore.InboundFaxInboxModel.filteringSettings);
        }, 1000);
    }

    public LoadDocument(documentId: string, annotationView: boolean = false): void {
        if (documentId.length <= 0) throw new TypeError("Document id required");

        const subOperation = annotationView ? SubServerOperations.Documents.DisplayDocumentAnnotationView : SubServerOperations.Documents.DisplayDocumentView;
        this.Execute(ServerOperations.Server.Myle_LoadDocument, { DocumentIds: [documentId] }, subOperation);
    }

    public CreateNewDocument(document: IDocumentCreateEditUIModel): void {
        const doc = document.get("MedicalDocumentInfo");
        const docModel = doc.Model;

        let requestData = {
            Id: GuidHelper.Empty,
            PatientLinksId: document.get("SelectedPatients").toArray().map(p => p.ContactId),
            ResponsibleProvidersId: document.get("SelectedClinicians").toArray().map(p => p.ContactId),
            Source: document.get("Source"),
            ReportClassId: document.get("ClassLookupId"),
            ReportSubClassId: document.get("SubClassLookupId"),
            Subject: document.get("Description"),
            UploadDate: docModel.UploadDate,
            ReceiptDate: cacheStore.Helper.CreateDateAsUTC(document.get("DocumentDate")),
            Notes: document.get("Note"),
            SiteId: docModel.SiteId,
            UploaderId: sessionStore.individualContactId,
            HRMStatusId: document.get("SelectedHRMStatus"),
            IsUrgent: docModel.IsUrgent,
            SendingFacilityId: document.get("SendingFacilityId"),
            SendingFacilityReport: document.get("SendingFacilityReport"),
            AccompanyingSubClass: document.get("AccompanyingSubClass"),
            AccompanyingMnemonic: document.get("AccompanyingMnemonic"),
            SourceAuthorName: document.get("SourceAuthorName"),
            AuthoringDateTime: MyleHelper.FormatDate(document.get("AuthoringDate")),
            ObservationDateTime: MyleHelper.FormatDate(document.get("ObservationDate"))
        };

        if (document.get("SelectedHRMStatus") != GuidHelper.Empty()) {
            requestData.HRMStatusId = document.get("SelectedHRMStatus");
        }
        else {
            delete requestData.HRMStatusId;
        }

        documentsStore.DocumentsModel = documentsStore.DocumentsModel.set("IsAttachInProcess", true);

        const operation = new LocalEvent(LocalOperations.Misc.FileUploader_Send);
        operation.AddItem("fileDescription", requestData);
        MiscDispatcher.dispatch(operation);
    }

    public UpdateExistingDocument(document: IDocumentCreateEditUIModel): void {
        let docModel = document.get("MedicalDocumentInfo").Model;

        let files = document.get("Files");

        let requestData = {
            Id: docModel.Id,
            PatientLinksId: document.get("SelectedPatients").toArray().map(p => p.ContactId),
            ResponsibleProvidersId: document.get("SelectedClinicians").toArray().map(p => p.ContactId),
            Source: docModel.Source,
            ReportClassId: docModel.ClassId,
            SetReportSubClassId: docModel.SubClassId,
            ReportSubClassId: docModel.SubClassId,
            Subject: docModel.Subject,
            ReceiptDate: cacheStore.Helper.CreateDateAsUTC(docModel.ReceiptDate),
            Notes: document.get("Note"),
            SiteId: docModel.SiteId,
            UploaderId: sessionStore.individualContactId,
            HRMStatusId: document.get("SelectedHRMStatus"),
            SignOffDate: MyleHelper.FormatDate(docModel.SignOffDate),
            SignOffById: docModel.SignOffByContactId,
            IsUrgent: docModel.IsUrgent,
            SendingFacilityId: document.get("SendingFacilityId"),
            SendingFacilityReport: document.get("SendingFacilityReport"),
            AccompanyingSubClass: document.get("AccompanyingSubClass"),
            AccompanyingMnemonic: document.get("AccompanyingMnemonic"),
            SourceAuthorName: document.get("SourceAuthorName"),
            AuthoringDateTime: MyleHelper.FormatDate(document.get("AuthoringDate")),
            ObservationDateTime: MyleHelper.FormatDate(document.get("ObservationDate")),
            PrescriptionRenewalId: docModel?.PrescriptionRenewal?.Id
        };

        if (document.get("SelectedHRMStatus") !== GuidHelper.Empty()) {
            requestData.HRMStatusId = document.get("SelectedHRMStatus");
        }
        else { delete requestData.HRMStatusId; }

        if (files && files.length > 0) {
            let operation = new LocalEvent(LocalOperations.Misc.FileUploader_Update);
            operation.AddItem("fileDescription", requestData);
            MiscDispatcher.dispatch(operation);
        }
        else
            this.Execute(ServerOperations.Server.Myle_UpdateUnapprouvedDocumentMetadataRequestResponse, requestData);
    }

    public UpdateDocumentMetadata(documentData: IDocumentInformation): void {
        const data = {
            Id: documentData.referenceId,
            Note: documentData.note,
            Source: documentData.source,
            ReportClassId: documentData.docClass,
            ReportSubClassId: documentData.docSubClass,
            Subject: documentData.description,
            ResponsibleProvidersIds: documentData.responsibleProvidersIds,
            ReceiptDate: cacheStore.Helper.CreateDateAsUTC(documentData.date),
            IsApproved: documentData.isApproved,
            PrescriptionRenewalId: documentData.prescriptionRenewalId
        };

        this.Execute(ServerOperations.Server.Myle_UpdateApprouvedDocumentMetadataRequestResponse, data, null, true);
    }

    public SaveDocumentDisplaySetting(settings: DocumentSearchSettings, isFromPatient: boolean): void {
        let userSetting = cacheStore.UserSettings;
        userSetting[isFromPatient ? "PatientDocumentSearchSettings" : "DocumentSearchSettings"] = settings;
        cacheStore.UserSettings = userSetting;

        this.Execute(
            ServerOperations.Server.Myle_UpdateUserSettings,
            {
                UserSettings: userSetting,
                UserId: sessionStore.userId
            }
        );
    }

    public SaveApprovedDocument(document: IDocumentCreateEditUIModel): void {
        let doc = document.get("MedicalDocumentInfo");
        let docModel = doc.Model;

        let sharedItemId = doc.getSharedItemId();

        let requestData = {
            DocumentId: docModel.Id,
            ReportClassId: docModel.ClassId,
            ReportSubClassId: docModel.SubClassId,
            Source: docModel.Source,
            ReceiptDate: cacheStore.Helper.CreateDateAsUTC(docModel.ReceiptDate),
            Subject: docModel.Subject,
            SharedItemId: sharedItemId
        };
        this.Execute(ServerOperations.Server.Myle_MedicalDocument_SaveApprovedDocument, requestData);
    }

    public SendDocumentToPortal(
        objectId: string,
        sharedItem: SharedItem,
        includeLinkedItem: boolean,
        hasBeenModifiedSinceLastShared: boolean
    ): void {
        let requestData = {
            ObjectId: objectId,
            SharedItemType: sharedItem.SharedItemType,
            Subject: sharedItem.Subject,
            PatientContactId: sharedItem.PatientContactId,
            IncludeLinkedItem: includeLinkedItem,
            Messages: sharedItem.Messages,
            SharedItemId: sharedItem.SharedItemId,
            LabMessageDTO: null,
            LinkedItemUpdated: hasBeenModifiedSinceLastShared,
            CanPatientReplyWithMessage: sharedItem.CanPatientReplyWithMessage
        };

        this.Execute(ServerOperations.Server.Myle_Patient_Portal_SyncSharedItem, requestData);
    }

    /**
     * Generate the annotated document then synchronize it in the portal
     * @param path The document path information of the document to synchronize
     */
    public async copyAnnotatedDocumentToPortal(path: DocumentPath): Promise<void> {
        await Client.postAsync(ServerOperations.Server.Myle_DownloadFile, { IgnoreAnnotations: false, ReturnFilePath: true, DocumentId: path.Id });
        this.Execute(
            ServerOperations.Server.Myle_Patient_Portal_CopyPermanentPdfForPortal,
            {
                PermanentPdfType: PortalLinkedItemType.Document,
                PermanentPdfId: path.Id,
                FileNameSigned: path.FileName,
                FileNameUnsigned: path.FileName,
                EncounterId: null,
                IsPermanentFile: false
            }
        );
    }

    public VerifyHasBatchScan(): void {
        let requestData = {
            EmployeeId: sessionStore.getCurrentEmployee().ContactId
        };

        this.Execute(ServerOperations.Server.Myle_MedicalDocuments_CheckIfHasBatchScan, requestData);
    }

    public StartNewBatchPojectClick(): void {
        let requestData = {
            EmployeeId: sessionStore.getCurrentEmployee().ContactId
        };

        this.Execute(ServerOperations.Server.Myle_MedicalDocuments_StartNewBatchProject, requestData);
    }

    public StartSplitPDFForBatchScan(batchId: string): void {
        let requestData = {
            EmployeeId: sessionStore.getCurrentEmployee().ContactId,
            BatchId: batchId
        };

        this.Execute(ServerOperations.Server.Myle_SplitPdf_Proxy_SplitPDF, requestData);
    }

    public GetBatchScanPages(): void {
        this.Execute(
            ServerOperations.Server.Myle_MedicalDocuments_GetBatchScanPagesInfo,
            { EmployeeId: sessionStore.getCurrentEmployee().ContactId }
        );
    }

    public enlargeClickedPage(pageNumber: number): void {
        let requestData = {
            PageNumber: pageNumber,
            EmployeeId: sessionStore.getCurrentEmployee().ContactId
        };

        this.Execute(ServerOperations.Server.Myle_MedicalDocuments_EnlargePage, requestData);
    }

    public CreateNewDocumentFromBatchScan(document: IDocumentCreateEditUIModel): void {
        let doc = document.get("MedicalDocumentInfo");
        let docModel = doc.Model;

        let requestData = {
            PatientLinksId: document.get("SelectedPatients").toArray().map(p => p.ContactId),
            ResponsibleProvidersId: document.get("SelectedClinicians").toArray().map(p => p.ContactId),
            Source: document.get("Source"),
            ReportClassId: document.get("ClassLookupId"),
            ReportSubClassId: document.get("SubClassLookupId"),
            Subject: document.get("Description"),
            UploadDate: docModel.UploadDate,
            ReceiptDate: cacheStore.Helper.CreateDateAsUTC(document.get("DocumentDate")),
            Notes: document.get("Note"),
            SiteId: docModel.SiteId,
            UploaderId: sessionStore.individualContactId,
            HRMStatusId: document.get("SelectedHRMStatus"),
            IsUrgent: docModel.IsUrgent,
            SendingFacilityId: document.get("SendingFacilityId"),
            SendingFacilityReport: document.get("SendingFacilityReport"),
            AccompanyingSubClass: document.get("AccompanyingSubClass"),
            AccompanyingMnemonic: document.get("AccompanyingMnemonic"),
            SourceAuthorName: document.get("SourceAuthorName"),
            AuthoringDateTime: MyleHelper.FormatDate(document.get("AuthoringDate")),
            ObservationDateTime: MyleHelper.FormatDate(document.get("ObservationDate")),
            Pages: document.get("SelectedPageArray").count() ? document.get("SelectedPageArray") : (document.get("SelectedBatchScanPages")?.map(p => p.pageNumber) ?? []),
            EmployeeContactId: sessionStore.getCurrentEmployee().ContactId
        };

        if (document.get("SelectedHRMStatus") != GuidHelper.Empty()) {
            requestData.HRMStatusId = document.get("SelectedHRMStatus");
        }
        else {
            delete requestData.HRMStatusId;
        }

        documentsStore.DocumentsModel = documentsStore.DocumentsModel.set("IsAttachInProcess", true);
        this.Execute(
            ServerOperations.Server.Myle_MedicalDocuments_AttachDocumentFromBatchScan,
            requestData,
            null,
            null,
            null,
            null,
            () => {
                documentsStore.DocumentsModel = documentsStore.DocumentsModel.set("IsAttachInProcess", false);
                if (cacheStore.ClinicSettings.EnablePrescriptionRenewal && document.get("ClassLookupId") === cacheStore.FindLookupByLookupCode(DocumentClass.MedicationAndPrescriptions).LookupID) {
                    fetchPrescriptionsRenewalCounters();
                }

                if (documentsStore.DocumentsModel.get("IsVisible") === false) {
                    documentsStore.SaveState();
                    LocalEvent.Dispatch(LocalOperations.Documents.RefreshBatchScanning);
                }
            }
        );
    }

    public onRemoveSingleBatchScan(pageNumber: number): void {
        let requestData = {
            PageNumber: pageNumber,
            EmployeeId: sessionStore.getCurrentEmployee().ContactId
        };

        this.Execute(ServerOperations.Server.Myle_MedicalDocuments_RemovePage, requestData);
    }

    public onRotatePage(pageNumber: number, orientation?: number, rotateToLeft?: boolean): void {
        let requestData = {
            PageNumber: pageNumber,
            RotateToLeft: rotateToLeft,
            Orientation: orientation,
            EmployeeId: sessionStore.getCurrentEmployee().ContactId
        };

        this.Execute(ServerOperations.Server.Myle_MedicalDocuments_RotatePage, requestData);
    }

    public RevokeDocumentToPortal(sharedItemId: string): void {
        const requestData = {
            SharedItemId: sharedItemId
        };

        this.Execute(ServerOperations.Server.Myle_Patient_Portal_RevokeSharedItem, requestData);
    }

    public unapproveDocument(documentId: string, onComplete: () => void = null): void {
        let requestData = {
            DocumentId: documentId
        };

        this.Execute(ServerOperations.Server.Myle_MedicalDocuments_RevokeApproval, requestData, null, null, null, null, onComplete);
    }

    public loadAttachedTasks(documentId: string): void {
        this.Execute(
            ServerOperations.Server.Myle_LoadAttachedCommunications,
            {
                DocumentId: documentId
            },
            SubServerOperations.AttachedCommunications.Documents
        );
    }
}

export { DocumentsAction };
